import { useQuery } from '@tanstack/react-query';
import {
  fetchCanDosBundle,
  getStudentProgressAnalytics,
  type StudentProgressAnalyticsOut,
} from '../../tutor/api/tutor.client';
import { useTutorQueryIdentity } from '../../tutor/hooks/useTutorQueryIdentity';
import { useFeatureAccess } from '../../../contexts/FeatureAccessContext';
import { useStudentMe } from '../../roster/hooks/useStudentMe';
import { useTutorTenantLabels } from '../../tutor/hooks/useTutorTenantLabels';
import {
  buildCanDoLookupFromLessons,
  type CanDoLookup,
} from '../../../components/student/studentLessonProgressFriendly';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';
import { progressAnalyticsQueryKey, STUDENT_DASHBOARD_ROOT } from '../queryKeys';
import {
  isDashboardIdentityReady,
  resolveDashboardStudentGrade,
} from '../utils/dashboardQueryGating';

export { STUDENT_DASHBOARD_ROOT };
export const STUDENT_PROGRESS_ANALYTICS_QUERY_KEY = [
  STUDENT_DASHBOARD_ROOT,
  'progress-analytics',
] as const;

export type ProgressAnalyticsPayload = {
  data: StudentProgressAnalyticsOut | null;
  canDoLookup: CanDoLookup;
};

export async function fetchProgressAnalyticsPayload(
  grade: number,
): Promise<ProgressAnalyticsPayload> {
  const out = await getStudentProgressAnalytics(grade);
  if (!out) {
    return { data: null, canDoLookup: new Map() };
  }
  const lessonNos = [
    ...new Set(
      (out.lessons || [])
        .map((l) => Number(l.lesson_no))
        .filter((n) => Number.isFinite(n) && n >= 1),
    ),
  ];
  if (lessonNos.length) {
    const bundles = await Promise.all(
      lessonNos.map((ln) =>
        fetchCanDosBundle(grade, ln).catch(() => ({ can_dos: [], communication_topics: [] })),
      ),
    );
    return { data: out, canDoLookup: buildCanDoLookupFromLessons(bundles) };
  }
  return { data: out, canDoLookup: new Map() };
}

const EMPTY_PAYLOAD: ProgressAnalyticsPayload = {
  data: null,
  canDoLookup: new Map() as CanDoLookup,
};

const STALE_TIME_MS = 5 * 60 * 1000;

/** Canonical owner for GET /v1/tutor/progress/analytics/student on the student dashboard. */
export function useStudentProgressAnalyticsData() {
  const { isSchoolFeatureEnabled } = useFeatureAccess();
  const { labels, loading: labelsLoading } = useTutorTenantLabels();
  const { grade, isRosterLoading } = useStudentMe();
  const { membershipKey, studentKey } = useTutorQueryIdentity();
  const studentGrade = resolveDashboardStudentGrade(grade);
  const identityReady = isDashboardIdentityReady(membershipKey, studentKey);

  const enabled =
    isSchoolFeatureEnabled('ai_tutor') &&
    !isRosterLoading &&
    !labelsLoading &&
    !labels.isMalay &&
    studentGrade !== undefined &&
    identityReady;

  const queryKey =
    studentGrade !== undefined && identityReady
      ? progressAnalyticsQueryKey({ membershipKey, studentKey, grade: studentGrade })
      : [...STUDENT_PROGRESS_ANALYTICS_QUERY_KEY, 'disabled'];

  const query = useQuery<ProgressAnalyticsPayload, Error>({
    queryKey,
    queryFn: () => fetchProgressAnalyticsPayload(studentGrade!),
    enabled,
    staleTime: STALE_TIME_MS,
  });

  const payload = query.data ?? EMPTY_PAYLOAD;
  const emptyMessage =
    !query.error && !payload.data
      ? 'Your Tamil Tutor progress will show up here after you finish a lesson. Ask your teacher if you are not sure which lesson to try first.'
      : null;

  return {
    enabled: isSchoolFeatureEnabled('ai_tutor'),
    data: payload.data,
    canDoLookup: payload.canDoLookup,
    loading: enabled && query.isLoading,
    isFetching: enabled && query.isFetching,
    error: query.error ? getApiErrorMessage(query.error) : null,
    emptyMessage,
    reload: () => void query.refetch(),
  };
}
